import type { OrderWithTimeline } from "../page";

interface Props {
  order: OrderWithTimeline;
}

interface BankStatusConfig {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
  description: string;
}

const BANK_STATUS: Record<string, BankStatusConfig> = {
  pending: {
    label: "بانتظار تأكيد التحويل",
    color: "text-orange-400",
    bg: "bg-orange-400/10",
    border: "border-orange-400/20",
    icon: "ri-time-line",
    description: "تم استلام طلبك بنجاح. ننتظر تأكيد استلام التحويل البنكي لتجهيز طلبك للشحن."
  },
  confirmed: {
    label: "تم تأكيد التحويل",
    color: "text-green-400",
    bg: "bg-green-400/10",
    border: "border-green-400/20",
    icon: "ri-checkbox-circle-line",
    description: "تم تأكيد استلام التحويل البنكي بنجاح. طلبك الآن قيد التجهيز للشحن."
  },
  shipped: {
    label: "تم تأكيد التحويل",
    color: "text-green-400",
    bg: "bg-green-400/10",
    border: "border-green-400/20",
    icon: "ri-checkbox-circle-line",
    description: "تم تأكيد استلام التحويل البنكي. طلبك في الطريق إليك!"
  },
  delivered: {
    label: "تم تأكيد التحويل",
    color: "text-green-400",
    bg: "bg-green-400/10",
    border: "border-green-400/20",
    icon: "ri-checkbox-circle-line",
    description: "تم تأكيد استلام التحويل البنكي وتم توصيل طلبك بنجاح."
  },
  cancelled: {
    label: "تم إلغاء الطلب",
    color: "text-red-400",
    bg: "bg-red-400/10",
    border: "border-red-400/20",
    icon: "ri-close-circle-line",
    description: "تم إلغاء هذا الطلب. إذا تم استلام التحويل سيتم إعادته خلال 3-5 أيام عمل."
  }
};

export default function BankTransferStatus({ order }: Props) {
  // Only show for bank transfer orders
  const isBankTransfer = 
    order.paymentLabel === "التحويل البنكي" || 
    order.paymentLabel?.includes("بنك") || 
    order.paymentLabel?.includes("تحويل");

  if (!isBankTransfer) return null;

  const status = BANK_STATUS[order.status] || BANK_STATUS.pending;
  const isPending = order.status === "pending";

  return (
    <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
      {/* Header with gradient */}
      <div className={`h-1 w-full ${
        isPending 
          ? "bg-gradient-to-r from-orange-400/0 via-orange-400/80 to-orange-400/0"
          : "bg-gradient-to-r from-green-400/0 via-green-400/80 to-green-400/0"
      }`} />

      <div className="p-6">
        {/* Title row */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${status.bg} border ${status.border}`}>
              <i className={`${status.icon} ${status.color} text-lg`}></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                حالة التحويل البنكي
              </h2>
              <p className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                طريقة الدفع: {order.paymentLabel}
              </p>
            </div>
          </div>

          {/* Status badge */}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${status.bg} ${status.border}`}>
            {isPending && (
              <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse flex-shrink-0"></span>
            )}
            <i className={`${status.icon} ${status.color} text-sm`}></i>
            <span className={`${status.color} text-xs font-bold whitespace-nowrap`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {status.label}
            </span>
          </div>
        </div>

        {/* Status description */}
        <div className={`rounded-xl border px-4 py-4 ${status.bg} ${status.border}`}>
          <p className={`${status.color} text-sm leading-relaxed`} style={{ fontFamily: "'Cairo', sans-serif" }}>
            {status.description}
          </p>
        </div>

        {/* Pending actions */}
        {isPending && (
          <div className="mt-4 space-y-3">
            <div className="bg-[#1a1a1a] border border-white/5 rounded-xl p-4">
              <p className="text-gray-400 text-xs mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-information-line text-amber-400"></i>
                ما الذي يحدث الآن؟
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-400/20 flex-shrink-0">
                    <i className="ri-check-line text-green-400 text-xs"></i>
                  </div>
                  <span className="text-gray-300 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    تم استلام طلبك بنجاح
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-400/20 flex-shrink-0">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                  </div>
                  <span className="text-amber-400 text-xs font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    ننتظر تأكيد استلام التحويل البنكي
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/5 flex-shrink-0">
                    <i className="ri-time-line text-gray-600 text-xs"></i>
                  </div>
                  <span className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    سيتم تجهيز طلبك للشحن فور التأكيد
                  </span>
                </div>
              </div>
            </div>

            {/* Contact info */}
            <div className="flex items-center gap-3 bg-[#1a1a1a] border border-white/5 rounded-xl px-4 py-3">
              <i className="ri-customer-service-2-line text-gray-500 text-sm flex-shrink-0"></i>
              <div className="flex-1">
                <p className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  هل أرسلت التحويل؟ تواصل معنا لتسريع التأكيد
                </p>
              </div>
              <a
                href={`https://wa.me/966541138847?text=${encodeURIComponent(`مرحباً، أرسلت تحويلاً لطلب رقم: ${order.orderRef} وأرغب في تأكيد استلامه`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 text-xs font-bold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                تواصل الآن
              </a>
            </div>
          </div>
        )}

        {/* Confirmed notice */}
        {!isPending && order.status !== "cancelled" && (
          <div className="mt-4 flex items-center gap-3 bg-green-400/5 border border-green-400/15 rounded-xl px-4 py-3">
            <div className="w-8 h-8 flex items-center justify-center bg-green-400/15 rounded-lg flex-shrink-0">
              <i className="ri-shield-check-line text-green-400 text-sm"></i>
            </div>
            <div>
              <p className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تم استلام المبلغ بنجاح
              </p>
              <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                طلبك قيد التجهيز وسيتم شحنه قريباً
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}